import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { useBackdrop } from '../../common/components/backDrop_context.component';
import { getLockedUsers } from './functions/get_locked_users.function';
import LoginLockoutTable from './login_lockout_table.component';
import { hasLoginLockoutManagementAccess } from '../../common/functions/permission_utils.function';

const parsePermissionNames = () => {
  try {
    const raw = localStorage.getItem('permissions');
    const list = JSON.parse(raw) || [];
    return list.map((p) => p.name);
  } catch {
    return [];
  }
};

export const LoginLockoutMainPage = () => {
  const navigate = useNavigate();
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const [lockedCount, setLockedCount] = useState(0);
  const [users, setUsers] = useState([]);
  const permissionNames = useMemo(() => parsePermissionNames(), []);
  const hasAccess = hasLoginLockoutManagementAccess(permissionNames);

  useEffect(() => {
    if (!hasAccess) {
      navigate('/rov', { replace: true });
    }
  }, [hasAccess, navigate]);

  useEffect(() => {
    if (hasAccess) {
      fetchLockedUsers();
    }
  }, [hasAccess]);

  const fetchLockedUsers = async () => {
    showBackdrop();
    try {
      const data = await getLockedUsers();
      const response = data?.response;
      setLockedCount(response?.count ?? 0);
      setUsers(response?.lockedUsers || []);
    } finally {
      hideBackdrop();
    }
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <DashboardLayout padding={true}>
      <Box sx={{ px: { xs: 2, sm: 4 }, pt: 2, pb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#192126', mb: 0.5 }}>
            Login Lockout Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View users locked after failed login attempts and unlock their accounts.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                border: '1px solid #E5E9EB',
                boxShadow: 'none',
                borderRadius: '6px',
              }}
            >
              <CardContent sx={{ pb: '12px !important' }}>
                <Typography fontSize="12px" color="text.secondary">
                  Locked users
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <LockIcon sx={{ color: '#DC2626', fontSize: 28 }} />
                  <Typography fontSize="28px" fontWeight={600} color="#121319">
                    {lockedCount}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <LoginLockoutTable users={users} fetchUsers={fetchLockedUsers} />
      </Box>
    </DashboardLayout>
  );
};
